import React, { useCallback } from 'react';
import Helmet from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';

import { dismissGoogleOneTap } from '../../actions/google-one-tap-actions';
import { showFieldErrors } from '../../actions/signup-actions';
import { SignupRecaptchaNotice } from './components';
import SocialLoginPanel from '../../components/SocialLoginPanel';
import Whitebox from '../../components/WhiteboxPage';
import { SignupFieldError } from '../../types';
import messages from './messages';
import { LoginLink } from './components';
import { useAnalyticsClient } from '../../utilities/analytics/analytics-web-client';
import { useAppDispatch, useAppSelector } from '../../stores/redux.store';
import { SignupPrivacyPolicy } from './components/SignupPagePrivacyPolicy';
import { FooterLogoWrapper } from './components/styled';
import { pageId } from './constants';
import SignupForm from './components/SignupForm';
import { useSignupSubmit } from './hooks/useSignupSubmit';
import { useSocialSignup } from './hooks/useSocialSignup';
import { useSignup } from './hooks/useSignup';
import { fetchPageState } from '../../utilities/hooks/useGlobalPageState';
import { checkFeatureGateOnceInitialized } from '../../utilities/feature-flags/feature-gate';
import { AidFrontendFeatureGate } from '../../providers/FeatureGates';
import Heading from '@atlaskit/heading';

const subjectIds: Required<SignupFieldError> = {
  email: 'selfSignupNoEmail',
};

export async function signupLoader({ request }: { request: Request }) {
  if (!(await checkFeatureGateOnceInitialized(AidFrontendFeatureGate.USE_STATE_DATA_LOADERS))) {
    return null;
  }
  return await fetchPageState('/rest/signup/state', request, {
    capabilities: ['capability-group--signup', 'capability-name--signup'],
  });
}

export const SignupPage = () => {
  const dispatch = useAppDispatch();

  const { screenSize } = useAppSelector(state => state.microbranding);

  const { formatMessage } = useIntl();
  const analyticsClient = useAnalyticsClient();

  const { error, userJourneyContextError } = useSignup();

  const { onSignup, email, fieldErrors, shouldDisableEmailInput } = useSignupSubmit();

  const handleValidationError = useCallback(
    (errors: SignupFieldError) => {
      const errorEventNames = Object.keys(errors).map(
        error => subjectIds[error] || 'selfSignupUnknownUiError'
      );
      errorEventNames.forEach(eventName => analyticsClient.errorShownEvent(pageId, eventName));
      dispatch(showFieldErrors(errors));
    },
    [analyticsClient, dispatch]
  );

  const handleFormClick = useCallback(() => {
    if (screenSize === 'mobile') {
      dispatch(dismissGoogleOneTap());
    }
  }, [dispatch, screenSize]);

  const onSubmitClick = useCallback(async () => {
    const isWebAuthnSupported = async () => {
      try {
        return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      } catch (err) {
        return false;
      }
    };

    analyticsClient.buttonClickedEvent(pageId, 'signupFormSubmit', {
      isWebAuthnSupported: await isWebAuthnSupported(),
    });
  }, [analyticsClient]);

  const {
    envSupportsSocialSignup,
    handleAppleLogin,
    handleGoogleLogin,
    handleMicrosoftLogin,
    handleSlackLogin,
  } = useSocialSignup();

  return (
    <>
      <Helmet title={formatMessage(messages.headTitle)} />
      <Whitebox
        pageId={pageId}
        header={
          <Heading as="h1" size="small">
            <FormattedMessage {...messages.headerTitle} />
          </Heading>
        }
        footer={
          <FooterLogoWrapper>
            <SignupRecaptchaNotice />
          </FooterLogoWrapper>
        }
        whiteboxFooter={<LoginLink />}>
        <SignupForm
          email={email}
          error={error}
          userJourneyContextError={userJourneyContextError}
          fieldErrors={fieldErrors}
          onSignup={onSignup}
          onValidationError={handleValidationError}
          onFormClick={handleFormClick}
          onSubmitClick={onSubmitClick}
          disableEmailInput={Boolean(email && shouldDisableEmailInput)}
          privacyPolicy={<SignupPrivacyPolicy />}
        />
        {envSupportsSocialSignup && (
          <SocialLoginPanel
            handleGoogleLogin={handleGoogleLogin}
            handleMicrosoftLogin={handleMicrosoftLogin}
            handleAppleLogin={handleAppleLogin}
            handleSlackLogin={handleSlackLogin}
          />
        )}
      </Whitebox>
    </>
  );
};

export default SignupPage;
